// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import IconTextLinkArrowRightInactive from "@tps/XDL/Icons/IconTextLinkArrowRightInactive";
import { createLinkIconStyles, mergeWithCss, xScreen } from "@tps/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface TitleTypographyProps {
    link?: string | LinkFieldValue;
    onLinkClick?: () => void;
    linkText?: string;
    openInNewTab?: boolean;
    extendedStyles?: TitleTypographyStyles;
    extraChild?: React.ReactNode;
}

type Props = TitleTypographyProps;

interface TitleTypographyLink {
    link?: LinkFieldValue;
    onClick?: () => void;
    url?: string;
    openInNewTab?: boolean;
    linkText?: string;
    styles: TitleTypographyStyles;
}

export interface TitleTypographyStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    link?: LinkPresentationProps;
}

const titleTypographyStyles: TitleTypographyStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: baseline;
            flex-wrap: wrap;
            width: 100%;
            margin-bottom: 20px;
            ${xScreen("lt", "sm")} {
                flex-direction: column;
                align-items: flex-start;
                gap: 16px;
                margin-bottom: 16px;
            }
        `,
    },
    title: {
        variant: "h2",
        color: "text.main",
        css: css`
            margin-right: 32px;
            margin-bottom: 0;
            ${xScreen("lt", "sm")} {
                margin-right: 0;
            }
        `,
    },
    link: {
        ...createLinkIconStyles(IconTextLinkArrowRightInactive, {
            icon: {
                position: "right",
            },
        }),
    },
};

const TitleLink: FC<TitleTypographyLink> = ({ url, openInNewTab, linkText, styles, onClick }) => {
    return (
        <Link
            href={url}
            onClick={(e: React.MouseEvent<Element, MouseEvent>) => {
                e.stopPropagation();
                onClick?.();
            }}
            target={openInNewTab ? "_blank" : undefined}
            {...styles.link}
            aria-label={`${linkText}`}
        >
            {linkText}
        </Link>
    );
};

const TitleTypographyLink: FC<TitleTypographyLink> = ({ link, ...otherProps }) => {
    const { url } = useGetLink(link ?? { type: "Page", value: "" });
    return <TitleLink {...otherProps} url={url} />;
};

const TitleTypography: FC<Props> = ({
    openInNewTab,
    linkText,
    extendedStyles,
    children,
    link,
    onLinkClick,
    extraChild,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(titleTypographyStyles, extendedStyles));
    const linkProps = {
        styles,
        openInNewTab,
        linkText,
    };

    const linkComponent = () => {
        if ((!link && !onLinkClick) || !linkText) {
            return null;
        }
        if (typeof link === "string") {
            return <TitleLink url={link} {...linkProps} />;
        }
        if (link && link.value) {
            return <TitleTypographyLink link={link} {...linkProps} />;
        }
        if (onLinkClick) {
            return <TitleLink onClick={onLinkClick} {...linkProps} />;
        }
        return null;
    };

    const linkElement = linkComponent();
    if (!React.Children.count(children) && !linkElement) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} {...otherProps}>
            <Typography {...styles.title}>{children}</Typography>
            {linkElement}
            {extraChild}
        </StyledWrapper>
    );
};

export default TitleTypography;
